/** 7 dias — alinhado ao EXPIRE_REFRESH_TOKEN padrão do server-eurobem */
export const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

export const AUTH_COOKIE_PATH = '/';

export const AUTH_COOKIE_PATHS = ['/', '/dashboard', '/clube', '/anunciante'];

export const AUTH_SESSION_EVENT = 'eurobem:auth-session';

const LS_TOKEN = 'eurobem.auth.token';
const LS_REFRESH = 'eurobem.auth.refreshtoken';
const LS_USER = 'eurobem.auth.user';
const LS_CLUB = 'eurobem.auth.club';
const LS_ADVERTISER = 'eurobem.auth.advertiser';
/** Sessão isolada nesta aba (ex.: suporte Eurobem sem derrubar o admin) */
const SS_TAB_SESSION = 'eurobem.auth.tab-session';
const LS_SUPPORT_HANDOFF = 'eurobem.auth.support-handoff';
const SUPPORT_HANDOFF_TTL_MS = 2 * 60 * 1000;

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const canUseSessionStorage = () =>
    typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const hasTabSession = () => canUseSessionStorage() && sessionStorage.getItem(SS_TAB_SESSION) === '1';

const activeStorage = () => (hasTabSession() ? sessionStorage : localStorage);

const readJson = key => {
    if (!canUseStorage()) return null;
    try {
        const raw = activeStorage().getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeJson = (key, value) => {
    if (!canUseStorage()) return;
    const store = activeStorage();
    if (value == null) {
        store.removeItem(key);
        return;
    }
    store.setItem(key, JSON.stringify(value));
};

export const getCookie = name => {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
    return null;
};

const readCookieToken = name => {
    const raw = getCookie(name);
    if (!raw) return null;
    const trimmed = raw.trim();
    if (trimmed.startsWith('eyJ')) return trimmed;
    try {
        return decodeURIComponent(trimmed);
    } catch {
        return trimmed;
    }
};

export const pickSessionUser = user => {
    if (!user) return null;
    return {
        id: user.id,
        userName: user.userName,
        profile: user.profile,
        role: user.role,
        clubId: user.clubId,
        advertiserId: user.advertiserId,
        super: user.super,
        support: Boolean(user.support) || user.role === 'support',
        team: user.team,
        Team: user.Team
            ? { id: user.Team.id, name: user.Team.name, status: user.Team.status }
            : undefined,
    };
};

export const persistAuthTokens = ({ token, refreshtoken }) => {
    if (!canUseStorage()) return;
    const store = activeStorage();
    if (token) store.setItem(LS_TOKEN, token);
    if (refreshtoken) store.setItem(LS_REFRESH, refreshtoken);
};

export const persistSessionUser = user => {
    writeJson(LS_USER, pickSessionUser(user));
};

export const persistSessionClub = club => {
    writeJson(LS_CLUB, club ?? null);
};

export const persistSessionAdvertiser = advertiser => {
    writeJson(LS_ADVERTISER, advertiser ?? null);
};

export const isAdvertiserContractSigned = advertiser => {
    if (!advertiser) return false;
    if (advertiser.approved === true) return true;
    if (advertiser.contractSigned === true) return true;
    if (advertiser.contract?.signedAt) return true;
    return advertiser.contract?.status === 'SIGNED' || advertiser.contract?.status === 'CONCLUDED';
};

export const persistAuthSession = ({ token, refreshtoken, user, club, advertiser }) => {
    persistAuthTokens({ token, refreshtoken });
    if (user) persistSessionUser(user);
    if (club !== undefined) persistSessionClub(club);
    if (advertiser !== undefined) persistSessionAdvertiser(advertiser);
};

/** Ativa sessão só nesta aba (sessionStorage), sem alterar o localStorage do admin. */
export const activateTabSession = ({ token, refreshtoken, user, club, advertiser }) => {
    if (!canUseSessionStorage()) return;
    sessionStorage.setItem(SS_TAB_SESSION, '1');
    persistAuthSession({ token, refreshtoken, user, club, advertiser });
};

/**
 * Admin grava handoff temporário e abre /clube?supportHandoff=nonce em nova guia.
 * Retorna o nonce para montar a URL.
 */
export const stageSupportHandoff = ({ token, refreshtoken, user, club }) => {
    if (!canUseStorage()) return null;
    const nonce =
        typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    localStorage.setItem(
        LS_SUPPORT_HANDOFF,
        JSON.stringify({
            nonce,
            createdAt: Date.now(),
            token,
            refreshtoken,
            user: pickSessionUser(user),
            club: club ?? null,
        }),
    );

    return nonce;
};

/**
 * Consome handoff da URL na aba do clube. Idempotente.
 * Deve rodar antes de ler a sessão no AuthProvider.
 */
export const consumeSupportHandoff = () => {
    if (!canUseStorage() || !canUseSessionStorage()) return false;

    const params = new URLSearchParams(window.location.search);
    const nonce = params.get('supportHandoff');
    if (!nonce) return false;

    const raw = localStorage.getItem(LS_SUPPORT_HANDOFF);
    localStorage.removeItem(LS_SUPPORT_HANDOFF);

    const cleanUrl = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('supportHandoff');
        const next = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState({}, '', next);
    };

    if (!raw) {
        cleanUrl();
        return false;
    }

    let payload;
    try {
        payload = JSON.parse(raw);
    } catch {
        cleanUrl();
        return false;
    }

    if (!payload?.nonce || payload.nonce !== nonce || !payload.token) {
        cleanUrl();
        return false;
    }

    if (Number(payload.createdAt) && Date.now() - Number(payload.createdAt) > SUPPORT_HANDOFF_TTL_MS) {
        cleanUrl();
        return false;
    }

    activateTabSession({
        token: payload.token,
        refreshtoken: payload.refreshtoken,
        user: payload.user,
        club: payload.club,
    });
    cleanUrl();
    return true;
};

export const clearAuthSession = () => {
    if (!canUseStorage()) return;

    if (hasTabSession()) {
        [LS_TOKEN, LS_REFRESH, LS_USER, LS_CLUB, LS_ADVERTISER, SS_TAB_SESSION].forEach(key =>
            sessionStorage.removeItem(key),
        );
        return;
    }

    [LS_TOKEN, LS_REFRESH, LS_USER, LS_CLUB, LS_ADVERTISER].forEach(key => localStorage.removeItem(key));
};

/** Importa tokens legados de cookies (paths antigos) para localStorage uma vez. */
export const migrateAuthFromCookies = () => {
    if (!canUseStorage()) return;
    if (hasTabSession()) return;
    if (localStorage.getItem(LS_TOKEN)) return;

    const token = readCookieToken('token');
    const refreshtoken = readCookieToken('refreshtoken');
    if (!token && !refreshtoken) return;

    persistAuthTokens({ token, refreshtoken });
};

export const getAuthToken = () => {
    if (canUseStorage()) {
        const stored = activeStorage().getItem(LS_TOKEN);
        if (stored) return stored;
    }
    return readCookieToken('token');
};

export const getRefreshToken = () => {
    if (canUseStorage()) {
        const stored = activeStorage().getItem(LS_REFRESH);
        if (stored) return stored;
    }
    return readCookieToken('refreshtoken');
};

export const getStoredUser = () => readJson(LS_USER);

export const getStoredClub = () => readJson(LS_CLUB);

export const getStoredAdvertiser = () => readJson(LS_ADVERTISER);

export const getCookiePath = () => AUTH_COOKIE_PATH;

export const cookieOptions = (path = AUTH_COOKIE_PATH) => ({
    path,
    maxAge: AUTH_COOKIE_MAX_AGE,
    sameSite: 'Lax',
});

export const isAdminUser = user =>
    user?.profile === 'admin' ||
    ['admin', 'manager', 'member'].includes(String(user?.team || '').toLowerCase()) ||
    user?.Team?.name === 'admin';

export const isClubUser = user => user?.profile === 'club';

export const isAdvertiserUser = user => user?.profile === 'advertiser';

export const mergeSessionUser = (previous, next) => {
    if (!next) return previous ?? null;
    const merged = !previous
        ? next
        : {
              ...previous,
              ...next,
              profile: next.profile ?? previous.profile,
              role: next.role ?? previous.role,
              club: next.club ?? previous.club,
              advertiser: next.advertiser ?? previous.advertiser,
              clubId: next.clubId ?? previous.clubId,
              advertiserId: next.advertiserId ?? previous.advertiserId,
              team: next.team ?? previous.team,
              Team: next.Team ?? previous.Team,
          };

    persistSessionUser(merged);
    return merged;
};

export const emitAuthTokensRefreshed = ({ token, refreshtoken }) => {
    persistAuthTokens({ token, refreshtoken });
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent(AUTH_SESSION_EVENT, {
            detail: { token, refreshtoken },
        }),
    );
};

export const emitAuthSessionLogout = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent(AUTH_SESSION_EVENT, {
            detail: { logout: true },
        }),
    );
};
